import {
  assumeWireReady,
  type ChartQueryPanel,
  toQueryPanel,
} from 'features/charts-v2/types/chart-query-panel'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import type { FilterService } from 'features/filters/service'
import type { FilterOption } from 'features/filters/types'

// TODO(PROD-2077-followup): replace with components['schemas']['FilterOptionsArgsV2']
// after BE PR #2543 merges and `api.generated.ts` is regenerated.
export type FilterOptionsV2Payload = {
  /** Narrowed to the query fields; the route reads only `filters` off it. */
  panel: ChartQueryPanel | null
  search_terms?: string[] | null
  search_criteria?: string[] | null
  fields?: string[] | null
  include_model?: boolean
  faceted?: boolean
}

/**
 * V2 sibling of `fetchFilterOptions`. Posts to `/post/filter/options/v2`,
 * sending the active panel state in the body so the backend can translate
 * panel filters into V1 criteria server-side and return per-panel counts.
 *
 * `search_terms` / `search_criteria` body-level args are an override for
 * callers that need to deviate from the panel — by default the panel
 * carries those values and we leave the body fields null.
 */
export const fetchFilterOptionsV2 = async (
  projectId: string,
  panel: ChartPanel | null,
  filterService: FilterService,
  options?: {
    fields?: string[]
    searchTerms?: string[]
    searchCriteria?: string[]
    includeModel?: boolean
    /** Faceted (the default, `true`) strips each requested field's own
     * filter before counting it, so a filter-picker still shows non-zero
     * counts for unselected values. A browsable row list — not a picker —
     * passes `false` so a field's own filter narrows its own counts too. */
    faceted?: boolean
  },
  signal?: AbortSignal,
): Promise<FilterOption[]> => {
  const body: FilterOptionsV2Payload = {
    // Only the query fields go over the wire — this route compiles the panel
    // through the same constraint pipeline /charts/chart uses.
    // Filter-option counts have never reflected semantic search, and
    // making them would change what this route returns. Asserted rather
    // than translated so the omission is visible.
    panel: panel && toQueryPanel(assumeWireReady(panel)),
    search_terms: options?.searchTerms ?? null,
    search_criteria: options?.searchCriteria ?? null,
    fields: options?.fields?.length ? options.fields : null,
    include_model: options?.includeModel ?? true,
    faceted: options?.faceted ?? true,
  }
  const response = await filterService.getFilterOptionsV2(projectId, body, {
    signal,
  })
  const opts = response?.filter_options
  if (!Array.isArray(opts)) return []
  return opts.map((opt) => ({ ...opt, field: opt.field }))
}
